import { useState, useMemo, useEffect, useRef } from "react";
import { StyleSheet, View, Modal } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import { PaperProvider } from "react-native-paper";
import { shadowStyles } from "../../../styles/shadow";
import ProgressBar from "../../adminDashboard/Analytics/components/ProgressBar";

const getNonLinearIncrement = () => {
  return Math.round(Math.random() * 10 + 1);
};

const baseDelay = 500;
// Helper function to get a non-linear delay
const getNonLinearDelay = (currentProgress: number) => {
  return baseDelay + currentProgress * 10;
};

export const SettingUpProjectWorkspaceModal = ({
  open,
  submission,
  onTasksReady,
}: {
  open: boolean;
  submission: any;
  onTasksReady: () => void;
}) => {
  const [modalOpen, setModalOpen] = useState(open);
  const [progress, setProgress] = useState<number>(10);
  const progressRef = useRef(10);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (submission?.totalTasks > 0 && onTasksReady) {
      console.log("SettingUpProjectWorkspaceModal: Tasks detected, calling onTasksReady");
      // Add a small delay to show the completion state briefly

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setTimeout(() => {
        progressRef.current = 100;
        setProgress(100);
      }, 500);

      setTimeout(() => {
        setModalOpen(false);
      }, 2000);

      const timeoutId = setTimeout(() => {
        onTasksReady();
      }, 2000); // 2 second delay to show completion

      return () => clearTimeout(timeoutId);
    }
  }, [submission?.totalTasks, onTasksReady]);

  useEffect(() => {
    // Cleanup function to clear any timeouts
    const cleanup = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };

    if (modalOpen && progressRef.current < 90) {
      const runProgress = () => {
        const increment = getNonLinearIncrement();
        const newProgress = Math.min(progressRef.current + increment, 90);
        progressRef.current = newProgress;
        setProgress(newProgress);

        if (newProgress < 90) {
          const delay = getNonLinearDelay(newProgress);
          timeoutRef.current = setTimeout(runProgress, delay);
        }
      };
      runProgress();
    }
    return cleanup;
  }, [modalOpen]);

  useEffect(() => {
    if (open) {
      setModalOpen(open);
      setProgress(0);
      progressRef.current = 0;
    }
  }, [open]);

  const progressStage = useMemo(() => Math.ceil(progress / 25), [progress]);

  return (
    <Modal
      id="custom-modal"
      animationType="fade"
      transparent={true}
      visible={modalOpen}
      role="dialog"
      aria-modal={true}
      aria-label={"loading-modal"}>
      <PaperProvider>
        <View style={styles.centeredView}>
          <View style={[styles.modal, shadowStyles.modal]}>
            <View style={styles.modalHeader}>
              <CustomText
                role="header"
                aria-level="1"
                text={"Setting up your project workspace"}
                size="s"
                textType="display"
                weight="ultraBold"
                style={styles.title}
                useTranslationText={false}
              />
              <CustomText
                aria-level="1"
                text={"We’re shaping your proposal into a project workspace that keeps you organized and moving."}
                size="s"
                textType="display"
                style={styles.subTitle}
                useTranslationText={false}
              />
            </View>
            <View style={styles.progressView}>
              <View style={styles.progressSection}>
                <CustomText
                  style={[styles.sectionLabel, progressStage > 0 && styles.activeProgressSection]}
                  text="Analyzing your proposal"
                  useTranslationText={false}
                />
                <ProgressBar percentage={progressStage > 1 ? 100 : progress * 4} size="sm" />
              </View>
              <View style={styles.progressSection}>
                <CustomText
                  style={[styles.sectionLabel, progressStage > 1 && styles.activeProgressSection]}
                  text="Drafting tasks and milestones"
                  useTranslationText={false}
                />
                <ProgressBar percentage={progressStage > 2 ? 100 : (progress - 25) * 4} size="sm" />
              </View>
              <View style={styles.progressSection}>
                <CustomText
                  style={[styles.sectionLabel, progressStage > 2 && styles.activeProgressSection]}
                  text="Mapping to rubric and due date"
                  useTranslationText={false}
                />
                <ProgressBar percentage={progressStage > 3 ? 100 : (progress - 50) * 4} size="sm" />
              </View>
              <View style={styles.progressSection}>
                <CustomText
                  style={[styles.sectionLabel, progressStage > 3 && styles.activeProgressSection]}
                  text="Setting up your project dashboard"
                  useTranslationText={false}
                />
                <ProgressBar percentage={progressStage > 4 ? 100 : (progress - 75) * 4} size="sm" />
              </View>
            </View>
            <View style={styles.tooltips}>
              <CustomText
                style={styles.tooltipText}
                text="Give us about a minute to build your workspace. Keep working and we’ll let you know when it’s ready."
                useTranslationText={false}
              />
            </View>
          </View>
        </View>
      </PaperProvider>
    </Modal>
  );
};

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  modal: {
    width: 653,
    borderRadius: 20,
    padding: 40,
    height: "auto",
    maxWidth: 900,
    display: "flex",
    alignItems: "center",
  },
  modalHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    alignItems: "center",
  },
  title: {
    textAlign: "center",
    fontSize: 24,
    lineHeight: 34,
  },
  subTitle: {
    width: 475,
    fontSize: 18,
    fontWeight: 400,
    lineHeight: 28,
    textAlign: "center",
  },
  progressView: {
    marginVertical: 40,
    display: "flex",
    flexDirection: "row",
    gap: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  progressSection: {
    width: 140,
  },
  sectionLabel: {
    fontSize: 14,
    textAlign: "center",
    color: "#667085",
  },
  activeProgressSection: {
    color: "#0084FF",
  },
  tooltips: {
    width: 462,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#0084FF",
    backgroundColor: "#0084FF1A",
  },
  tooltipText: {
    color: "#006DD2",
    fontSize: 14,
    lineHeight: 20,
  },
});
